import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import "./tooltip.css";

const HIDE_DELAY_MS = 120;

export type TooltipSide = "above" | "below";

/**
 * Keep an info bubble inside the viewport, on the side of the mark that has
 * more room, so a following card cannot cover it and a long hint can scroll.
 */
export function placeTooltip(
  mark: Pick<DOMRect, "top" | "bottom" | "left" | "width">,
  bubble: { width: number; height: number },
  viewport: { width: number; height: number },
  gap: number,
): { top: number; left: number; maxHeight: number; side: TooltipSide } {
  const below = Math.max(0, viewport.height - mark.bottom - gap * 2);
  const above = Math.max(0, mark.top - gap * 2);
  const side: TooltipSide = below >= above ? "below" : "above";
  const available = side === "below" ? below : above;
  const maxHeight = available > 0 ? available : Math.max(0, viewport.height - gap * 2);
  const height = Math.min(bubble.height, maxHeight > 0 ? maxHeight : bubble.height);
  const width = Math.min(bubble.width, Math.max(0, viewport.width - gap * 2));

  let top = side === "below" ? mark.bottom + gap : mark.top - gap - height;
  const minTop = gap;
  const maxTop = Math.max(gap, viewport.height - gap - height);
  top = Math.min(Math.max(top, minTop), maxTop);

  let left = mark.left + mark.width / 2 - width / 2;
  const minLeft = gap;
  const maxLeft = Math.max(gap, viewport.width - gap - width);
  left = Math.min(Math.max(left, minLeft), maxLeft);

  return { top, left, maxHeight, side };
}

function readGapPx(el: Element): number {
  const raw = getComputedStyle(el).getPropertyValue("--space-2").trim();
  const value = parseFloat(raw);
  if (!Number.isFinite(value)) return 8;
  if (raw.endsWith("rem")) return value * parseFloat(getComputedStyle(el).fontSize);
  return value;
}

function TooltipBubble({
  id,
  text,
  anchorRef,
  onEnter,
  onLeave,
}: {
  id: string;
  text: string;
  anchorRef: RefObject<HTMLElement | null>;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const [box, setBox] = useState<{ top: number; left: number; maxHeight: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const bubble = bubbleRef.current;
    if (!anchor || !bubble) return;

    const place = () => {
      const gap = readGapPx(document.documentElement);
      const next = placeTooltip(
        anchor.getBoundingClientRect(),
        { width: bubble.offsetWidth, height: bubble.scrollHeight },
        { width: window.innerWidth, height: window.innerHeight },
        gap,
      );
      setBox({ top: next.top, left: next.left, maxHeight: next.maxHeight });
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorRef, text]);

  return createPortal(
    <span
      ref={bubbleRef}
      id={id}
      className={box ? "ui-tooltip-bubble is-placed" : "ui-tooltip-bubble"}
      role="tooltip"
      style={
        box
          ? { top: box.top, left: box.left, maxHeight: box.maxHeight }
          : undefined
      }
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {text}
    </span>,
    document.body,
  );
}

function useTooltipOpen() {
  const [open, setOpen] = useState(false);
  const hideTimer = useRef(0);

  const show = () => {
    window.clearTimeout(hideTimer.current);
    setOpen(true);
  };
  const scheduleHide = () => {
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setOpen(false), HIDE_DELAY_MS);
  };

  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  return { open, show, scheduleHide };
}

function InfoMark({
  text,
  label,
  children,
}: {
  text: string;
  label?: string;
  children?: ReactNode;
}) {
  const id = useId();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { open, show, scheduleHide } = useTooltipOpen();

  return (
    <span className="ui-tooltip" onMouseEnter={show} onMouseLeave={scheduleHide} onFocus={show} onBlur={scheduleHide}>
      {children}
      <button
        ref={anchorRef}
        type="button"
        className="ui-tooltip-mark"
        aria-label={label ?? "More about this"}
        aria-describedby={open ? id : undefined}
      >
        i
      </button>
      {open ? (
        <TooltipBubble id={id} text={text} anchorRef={anchorRef} onEnter={show} onLeave={scheduleHide} />
      ) : null}
    </span>
  );
}

export function Tooltip({
  text,
  label,
  children,
}: {
  text: string;
  label?: string;
  children: ReactNode;
}) {
  return (
    <InfoMark text={text} label={label}>
      {children}
    </InfoMark>
  );
}

/** Label text with an optional info-mark tooltip beside it. */
export function LabelWithHint({
  label,
  tooltip,
}: {
  label: string;
  tooltip?: string;
}) {
  if (!tooltip) return <span>{label}</span>;
  return (
    <Tooltip text={tooltip} label={`About ${label}`}>
      <span>{label}</span>
    </Tooltip>
  );
}

/**
 * Standalone info mark + bubble, with no wrapping element. Use beside a
 * `<label htmlFor>` instead of `Tooltip`/`LabelWithHint` so the mark's own
 * aria-label doesn't get folded into the label's accessible name.
 */
export function HintMark({ text, label }: { text: string; label?: string }) {
  return <InfoMark text={text} label={label} />;
}
